import * as THREE from 'three'
import { get } from 'svelte/store'
import { storeScene } from '../stores/scene'
import { ThreeItemKeys } from '../constants/three'

const SELECTION_HELPER_KEY = '__selectionBoxHelper'
const SELECTION_COLOR = new THREE.Color(0x3b82f6)

// The helper is parented to the selected object so it inherits its position/rotation/scale
// from the scene graph automatically. Box3.setFromObject bakes the object's own transform
// into its result though, so reusing that box as-is would apply the transform twice once
// parented. Instead, compute the box as if the object sat at the origin — that isolates its
// own local geometry/children, and the object's real transform then applies exactly once.
const computeLocalBox = (object: THREE.Object3D): THREE.Box3 => {
  const position = object.position.clone()
  const quaternion = object.quaternion.clone()
  const scale = object.scale.clone()

  object.position.set(0, 0, 0)
  object.quaternion.identity()
  object.scale.set(1, 1, 1)
  object.updateWorldMatrix(true, true)

  const box = new THREE.Box3().setFromObject(object)
  if (object.parent) box.applyMatrix4(object.parent.matrixWorld.clone().invert())

  object.position.copy(position)
  object.quaternion.copy(quaternion)
  object.scale.copy(scale)
  object.updateWorldMatrix(true, true)

  return box
}

export const applySelectionMaterial = (object: THREE.Object3D) => {
  const scene = get(storeScene)
  if (!scene) return

  const helper = new THREE.Box3Helper(computeLocalBox(object), SELECTION_COLOR)
  helper.userData[ThreeItemKeys.TRANSIENT] = true
  // Purely decorative outline — must never be hit by the selection raycaster,
  // or clicking near its wireframe gets treated as a click on an untagged
  // object and falls back to selecting the (locked) parent group.
  helper.raycast = () => {}
  object.add(helper)
  object.userData[SELECTION_HELPER_KEY] = helper.uuid
}

export const restoreOriginalMaterial = (object: THREE.Object3D) => {
  const scene = get(storeScene)
  if (!scene) return

  const helperUuid = object.userData[SELECTION_HELPER_KEY]
  if (helperUuid) {
    scene.getObjectByProperty('uuid', helperUuid)?.removeFromParent()
    delete object.userData[SELECTION_HELPER_KEY]
  }
}

export const updateSelectionBox = (object: THREE.Object3D) => {
  const scene = get(storeScene)
  if (!scene) return

  const helperUuid = object.userData[SELECTION_HELPER_KEY]
  if (!helperUuid) return

  const helper = scene.getObjectByProperty('uuid', helperUuid) as THREE.Box3Helper | undefined
  if (!helper) return

  // The helper is a child of `object` — detach it first so its own (stale) wireframe
  // geometry isn't picked up by computeLocalBox's traversal.
  helper.removeFromParent()
  helper.box.copy(computeLocalBox(object))
  object.add(helper)
}
